import classnames from 'classnames';
import React, { useEffect, useState } from 'react';

import { Icon } from '../../../../../../components/Icon/Icon';
import { Modal } from '../../../../../../components/Modal/Modal';
import { PersonalIdentifier } from '../../../../../../components/PersonalIdentifier/PersonalIdentifier';
import { SeatInfo } from '../../../../../../components/SeatInfo/SeatInfo';
import { SortableList } from '../../../../../../components/SortableList/SortableList';
import { graphql } from '../../../../../../generated';
import { t } from '../../../../../../locales/translate';
import { apollo, useQueryHandler } from '../../../../../../model/graphql';
import { Selection, downloadFiles, mergePDF } from '../../../../../../util/util';
import { TicketPersonalizationForm } from './TicketPersonalizationForm';
import { notNil } from '../../../../../../util/values';
import { PrintingOrderQuery } from '../../../../../../generated/graphql';
import { useSequentialPolling } from '../../../../../../model/data';

export const PRINT_ORDER = graphql(`
  query PrintingOrder($eventId: ID!, $printingOrderId: ID!) {
    event(id: $eventId) {
      id
      printingOrder(id: $printingOrderId) {
        id
        items {
          nodes {
            id
            validationCode
            personalIdentifier
            invalidated
            venueSectionId
            url
            seat {
              row
              name
              venueSection {
                name
              }
            }
          }
          pageInfo {
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    }
  }
`);

type PrintingOrder = Pick<NonNullable<PrintingOrderQuery['event']>, 'printingOrder'>;
export type PrintedTicket = NonNullable<NonNullable<PrintingOrder['printingOrder']>['items']['nodes']>[0];

interface OrderTicketsProps {
  printingOrderId: string;
  eventId: string;
  selectedTicketIds: Selection<string>;
  selectedOrderIds: Selection<string>;
  onTicketUpdate: (info: { venueSectionId: string | null }) => void;
  onDeleteTicket: (ticketId: string, tickets: PrintedTicket[]) => void;
  onTicketsLoaded: (tickets: PrintedTicket[]) => void;
}

function getValidTickets(tickets: PrintedTicket[]): PrintedTicket[] {
  return tickets.filter((ticket) => !ticket.invalidated);
}

function orderIsMissingTicketUrls(data?: PrintingOrderQuery): boolean {
  const validTickets = getValidTickets(data?.event?.printingOrder?.items.nodes || []);
  return validTickets.some((ticket) => !ticket.url);
}

async function downloadTickets(urls: string[]): Promise<void> {
  if (urls.length === 1) {
    await downloadFiles([{ url: urls[0] }]);
  } else {
    await downloadFiles([
      {
        blob: await mergePDF(urls),
        filename: 'tickets',
      },
    ]);
  }
}

export function OrderTickets({
  eventId,
  printingOrderId,
  selectedTicketIds,
  selectedOrderIds,
  onTicketUpdate,
  onDeleteTicket,
  onTicketsLoaded,
}: OrderTicketsProps): JSX.Element | null {
  const [editRow, setEditRow] = useState<string>();

  const { data, loading, refetch } = useQueryHandler(PRINT_ORDER, {
    variables: {
      eventId,
      printingOrderId,
    },
    onCompleted: (data) => {
      onTicketsLoaded(data?.event?.printingOrder?.items.nodes || []);
    },
  });

  const tickets = data?.event?.printingOrder?.items.nodes?.filter(notNil) || [];

  const validTickets = getValidTickets(tickets);

  const invalidated = !validTickets.length;

  const polling = useSequentialPolling({
    load: async () => {
      const result = apollo.query({
        query: PRINT_ORDER,
        variables: {
          eventId,
          printingOrderId,
        },
      });
      return result;
    },
    interval: 5,
    onUpdate: (result) => {
      return orderIsMissingTicketUrls(result.data);
    },
  });

  useEffect(() => {
    if (orderIsMissingTicketUrls(data)) {
      polling.start();
    }
  }, [orderIsMissingTicketUrls(data)]);

  useEffect(() => {
    if (!!selectedTicketIds.size && selectedTicketIds.hasEvery(validTickets?.map((t) => t?.id) || [])) {
      selectedOrderIds.add(printingOrderId);
    } else {
      selectedOrderIds.remove(printingOrderId);
    }
  }, [selectedTicketIds.size]);

  const editingTicket = editRow ? tickets.find((e) => e?.id === editRow) : undefined;

  function handleOnTicketUpdateSuccess(): void {
    setEditRow(undefined);
    onTicketUpdate({ venueSectionId: editingTicket?.venueSectionId ?? null });
    refetch();
  }

  function toggleSelectAll(): void {
    if (selectedTicketIds.size === validTickets.length) {
      selectedTicketIds.remove(tickets.map((e) => e?.id));
    } else {
      selectedTicketIds.add(validTickets.map((e) => e?.id));
    }
  }

  return (
    <>
      <SortableList<(typeof tickets)[0] & { actions?: JSX.Element }>
        loading={!data && loading && !polling.running}
        columns={[
          {
            dataKey: 'validationCode',
            label: t.model.ticket.ticketNumber,
            cellRenderer: (row) => (
              <span className={classnames({ strikethrough: row.invalidated })}>{row.validationCode}</span>
            ),
          },
          {
            dataKey: 'personalIdentifier',
            label: t.model.event.personalization._name,
            width: '100%',
            cellRenderer: (row) => (
              <div className={classnames('print-history__personal-identifier', { strikethrough: row.invalidated })}>
                <div>
                  <PersonalIdentifier value={row.personalIdentifier} />
                  {row.seat && <SeatInfo seat={row.seat} />}
                </div>
                {!row.invalidated && <Icon name="edit" onClick={() => setEditRow(row.id)} />}
              </div>
            ),
          },
          {
            dataKey: 'actions',
            width: '100%',
            align: 'right',
            label: invalidated ? null : (
              <div className="print-history-line__actions">
                <Icon
                  name="download"
                  loading={loading || polling.running}
                  onClick={() => downloadTickets(validTickets.map((node) => node?.url as string))}
                />
              </div>
            ),
            cellRenderer: (row) => {
              if (row.invalidated) return null;
              return (
                <div className="print-history-line__actions">
                  <Icon name="trash" tooltip={t.general.return} onClick={() => onDeleteTicket(row.id, tickets)} />
                  <a href={row.url || '#'} target="_blank" rel="noreferrer">
                    <Icon name="download" loading={!row.url} />
                  </a>
                </div>
              );
            },
          },
        ]}
        items={tickets}
        selectedItems={tickets?.filter((tick) => selectedTicketIds.has(tick?.id))}
        multiSelect
        onRowSelect={(row) => {
          selectedTicketIds.toggle(row.id);
        }}
        onSelectAll={toggleSelectAll}
        size="sm"
        disableSelectForRow={(row) => row.invalidated}
      />
      <Modal title={t.model.ticket.updatePersonalIdentifier} open={!!editRow} size="small">
        {() => (
          <TicketPersonalizationForm
            ticket={{
              id: editingTicket?.id ?? '',
              personalIdentifier: editingTicket?.personalIdentifier ?? '',
            }}
            onClose={() => setEditRow(undefined)}
            onSuccess={handleOnTicketUpdateSuccess}
          />
        )}
      </Modal>
    </>
  );
}
